///
/// EarthBrowser
///
/// Storage Manager Browser Feature
///
/// Manages Storage Manager window lifecycle and command palette registration (dev mode only)
///
/// Created by Matt Giger on December 21, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useEffect } from 'react'
import { HardDrive } from 'lucide-react'
import { StorageManagerBrowserRenderer } from '@/components/features/storage-manager-browser/StorageManagerBrowser'
import { CommandRegistry } from '@/components/features/CommandRegistry'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { isDevelopment } from '@/utils/environment'
import { openOrRestoreWindow } from '@/utils/windowUtils'

const STORAGE_MANAGER_WINDOW_ID = 'storage-manager-browser'

interface StorageManagerBrowserFeatureProps {
	devtoolsUnlocked: boolean
}

export function StorageManagerBrowserFeature({ devtoolsUnlocked }: StorageManagerBrowserFeatureProps) {
	const dispatch = useAppDispatch()
	const datasetWindows = useAppSelector(state => state.dialog.datasetWindows)
	const storageManagerWindow = datasetWindows[STORAGE_MANAGER_WINDOW_ID]

	// Register with command palette (only in dev mode)
	// Shows in dev mode OR when unlocked via long press
	useEffect(() => {
		const shouldShow = isDevelopment() || devtoolsUnlocked
		if (!shouldShow) return

		const unregister = CommandRegistry.registerFeature({
			id: 'storage-manager-browser',
			command: {
				id: 'storage-manager',
				title: 'Storage Manager',
				icon: <HardDrive className="w-4 h-4 text-purple-400" />,
				category: 'Developer',
				enabled: true,
		onSelect: () => {
			openOrRestoreWindow(
				dispatch,
				datasetWindows,
				STORAGE_MANAGER_WINDOW_ID,
				{
					id: STORAGE_MANAGER_WINDOW_ID,
					title: 'Storage Manager',
					windowType: 'storage-manager',
					x: 200,
					y: 150,
					width: 900,
					height: 700,
					isMaximized: false,
					isVisible: true,
				}
			)
		}
			},
			renderBrowser: () => null // Not needed for window-based browser
		})

		return unregister
	}, [dispatch, devtoolsUnlocked, datasetWindows])

	// Render the window if it's open
	if (!storageManagerWindow) {
		return null
	}

	return (
		<StorageManagerBrowserRenderer
			windowState={storageManagerWindow}
		/>
	)
}

